import Vue from 'vue';
import Router from 'vue-router';
import { getStorageItem } from './helpers/persistence';
import store from './store';

// Function to check if we have a selected device in localstorage
const getCurrentDevice = () => getStorageItem('currentDevice') || {};

Vue.use(Router);

/***
 * Vue router first calls the global route guard `beforeEach` and aftwards invokes possible
 * route based guards. We take leverage of this order to first check if a route requires an
 * authenticated user and afterwards check if a route requires a selected device.
 */
const router = new Router({
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
  routes: [
    {
      path: '*',
      redirect: '/'
    },
    {
      path: '/error',
      name: 'error',
      component: () => import(/* webpackChunkName: "error" */ './components/views/Error.vue'),
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/dbug',
      name: 'dbug',
      component: () =>
        import(/* webpackChunkName: "chunk-page-login" */ './components/views/Login.vue'),
      meta: {}
    },
    {
      path: '/',
      name: 'home',
      component: () =>
        import(/* webpackChunkName: "chunk-page-home" */ './components/views/Home.vue'),
      meta: {
        requiresAuthentication: true
      },
      beforeEnter: requireSelectedDevice
    },
    {
      path: '/history',
      name: 'history',
      component: () =>
        import(/* webpackChunkName: "chunk-page-history" */ './components/views/History.vue'),
      meta: {
        requiresAuthentication: true
      },
      beforeEnter: requireSelectedDevice
    },
    {
      path: '/devicelist',
      name: 'devicelist',
      component: () =>
        import(/* webpackChunkName: "chunk-page-devicelist" */ './components/views/DeviceList.vue'),
      meta: {
        requiresAuthentication: true
      },
      beforeEnter: function(to, from, next) {
        if (is_debug_mode()) {
          //if the url has query (?query)

          next();
        } else {
          next('/dbug');
        }
      }
    },
    {
      path: '/profile',
      name: 'profile',
      component: () =>
        import(/* webpackChunkName: "chunk-page-profile" */ './components/views/Profile.vue'),
      meta: {
        requiresAuthentication: true
      },
      beforeEnter: function(to, from, next) {
        if (is_debug_mode()) {
          //if the url has query (?query)

          next();
        } else {
          next('/dbug');
        }
      }
    },
    {
      path: '/system',
      name: 'system',
      component: () =>
        import(/* webpackChunkName: "chunk-page-system" */ './components/views/System.vue'),
      meta: {
        requiresAuthentication: true
      },
      beforeEnter: requireSelectedDevice
    },
    {
      path: '/servicemode',
      name: 'servicemode',
      component: () =>
        import(
          /* webpackChunkName: "chunk-page-servicemode" */ './components/views/ServiceMode.vue'
        ),
      meta: {
        requiresAuthentication: true
      },
      beforeEnter: requireSelectedDevice
    },
    {
      path: '/expertview',
      name: 'expertview',
      component: () =>
        import(/* webpackChunkName: "chunk-page-expertview" */ './components/views/ExpertView.vue'),
      meta: {
        requiresAuthentication: true
      },
      beforeEnter: requireSelectedDevice
    },
    {
      path: '/test',
      name: 'test',
      component: () =>
        import(/* webpackChunkName: "chunk-page-testview" */ './components/expertview/index.vue'),
      meta: {
        requiresAuthentication: false
      }
    },
    {
      path: '/news',
      name: 'news',
      component: () =>
        import(/* webpackChunkName: "chunk-page-news" */ './components/views/News.vue'),
      meta: {}
    },
    {
      path: '/imprint',
      name: 'imprint',
      component: () =>
        import(/* webpackChunkName: "chunk-page-imprint" */ './components/views/Imprint.vue'),
      meta: {}
    },
    {
      path: '/privacyagreement',
      name: 'privacyagreement',
      component: () =>
        import(
          /* webpackChunkName: "chunk-page-privacyagreement" */ './components/views/PrivacyAgreement.vue'
        ),
      meta: {}
    },
    {
      path: '/termsandconditions',
      name: 'termsandconditions',
      component: () =>
        import(
          /* webpackChunkName: "chunk-page-termsandconditions" */ './components/views/TermsAndCondition.vue'
        ),
      meta: {}
    },
    {
      path: '/oss',
      name: 'oss',
      component: () =>
        import(/* webpackChunkName: "chunk-page-oss" */ './components/views/Oss.vue'),
      meta: {}
    },
    {
      path: '/confirm',
      name: 'confirm',
      component: () =>
        import(/* webpackChunkName: "chunk-page-confirm" */ './components/views/ConfirmSignUp.vue'),
      meta: {}
    },
    {
      path: '/changepassword',
      name: 'changepassword',
      component: () =>
        import(
          /* webpackChunkName: "chunk-page-changepassword" */ './components/views/ChangePassword.vue'
        ),
      meta: {}
    },
    {
      path: '/resetpassword',
      name: 'resetpassword',
      component: () =>
        import(
          /* webpackChunkName: "chunk-page-resetpassword" */ './components/views/ResetPassword.vue'
        ),
      meta: {}
    }
  ]
});

/**
 * This function checks if a target route requires a selected device.
 * If a device is required, this function tries to retrieve it from the store
 * and proceeds. If no device is retrieved, we redirect a user to the devicelist.
 *
 * @param {object} to - target route
 * @param {object} from - object of current route from which a route-change happens
 * @param {function} next - function to trigger route change
 */

function is_debug_mode() {
  const adminUserList = [
    'marcos.lourenco_neves#siemens.com',
    'elias.kyrlies#siemens.com',
    'rodrigo.pinheiro#siemens.com',
    'fabio.matavelli#siemens.com',
    'alina.feldmeier#siemens.com',
    'andre.dias_sao_martinho_gomes#siemens.com',
    'rui.dias_roque#siemens.com',
    'bruno.costa_morgado#siemens.com',
    'susanne.forster#siemens.com',
    'rita.coutinho#siemens.com',
    'narciso.andre#siemens.com',
    'lucas.pina-rodrigues#siemens.com',
    'narciso.andre#siemens.com',
    'jorge.carola#siemens.com',
    'weiss.alexander#siemens.com'
  ];

  const user = store.getters.user();
  const filtered = adminUserList.filter(e => e == user.username);

  console.log('adminUserList');
  console.log(adminUserList);

  console.log('===============================');

  console.log('USER');
  console.log(user);

  console.log('===============================');
  console.log('filtered');
  console.log(filtered);

  if (filtered.length > 0) {
    return true;
  } else {
    // window.location.href = 'https://new.siemens.com/de/de/produkte/fuer-den-privathaushalt/junelight.html';
    return false;
  }
}

function requireSelectedDevice(to, from, next) {
  /**
   * Before a route can be entered, we have to check if this route requires
   * a device. If it does, we try to see if there is already one in the localstorage and proceed.
   * If there is no device, we first have to query the devicelist and have the user select one.
   */

  if (is_debug_mode()) {
    //if the url has query (?query)

    // PROCEED
    Vue.$log.info('router::requireSelectedDevice() -> getCurrentDevice()', getCurrentDevice());
    if (getCurrentDevice().serial) {
      store.commit('SET_DEVICE', getCurrentDevice());
      next();
    } else {
      next('/devicelist');
    }
  } else {
    next('/dbug');
  }
}

/**
 * Reusable function which simply checks if the target route is protected and whether a user
 * is allowed to proceed or not. Checking if a user is allowed or not is determined by the
 * presence of user object in our Vuex store. If it returns undefined, we assume that the
 * user hasn't authenticated, yet.
 *
 * @param {object} to - target route
 * @param {object} from - object of current route from which a route-change happens
 * @param {function} next - function to trigger route change
 */
function requiresAuth(to, from, next) {
  // Function to check if we have a valid user and then proceed with a route change
  function proceed() {
    if (store.getters.user()) {
      next();
    } else {
      next('/login');
    }
  }

  // Check if target route requires an authenticated user
  if (to.meta.requiresAuthentication) {
    // Check if Vuex store has not user, yet
    if (!store.getters.user()) {
      store.dispatch('getProfile').then(() => {
        proceed();
      });
    } else {
      proceed();
    }
  } else {
    next();
  }
}

router.beforeEach((to, from, next) => {
  requiresAuth(to, from, next);
});

export default router;
